import { useMemo } from 'react';

import { ErrorLike, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useSelector } from 'react-redux';

import { selectAccountId, selectBrainId } from '@/redux/session/selectors';
import { getAnalyticsRangeUTC } from '@/util/analytics';

interface SystemInsightsPerformanceRow {
  avg_csat_brain: number | null;
  csat_brain_count: number;
  goal_achieved_percentage: number | null;
  goal_achieved_count: number;
  goal_total_count: number;
}

interface SystemInsightsPerformanceData {
  get_system_insights_performance: SystemInsightsPerformanceRow[];
}

interface SystemInsightsPerformanceResult {
  isLoading: boolean;
  error?: ErrorLike;
  avgCsatBrain: number | null;
  csatBrainCount: number;
  goalAchievedPercentage: number | null;
  goalAchievedCount: number;
  goalTotalCount: number;
}

interface UseSystemInsightsPerformanceProps {
  startDate: string;
  endDate: string;
  skip?: boolean;
}

export const SYSTEM_INSIGHTS_PERFORMANCE = gql`
  query GetSystemInsightsPerformance(
    $accountId: uuid
    $startDate: timestamp
    $endDate: timestamp
    $brainIds: _uuid
  ) {
    get_system_insights_performance(
      args: {
        account_id: $accountId
        start_time: $startDate
        end_time: $endDate
        brain_parent_ids: $brainIds
      }
    ) {
      avg_csat_brain
      csat_brain_count
      goal_achieved_percentage
      goal_achieved_count
      goal_total_count
    }
  }
`;

export const useSystemInsightsPerformance = ({
  startDate,
  endDate,
  skip = false,
}: UseSystemInsightsPerformanceProps): SystemInsightsPerformanceResult => {
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);

  const variables = useMemo(
    () => ({
      ...getAnalyticsRangeUTC(startDate, endDate),
      accountId,
      brainIds: brainId ? `{${brainId}}` : undefined,
    }),
    [accountId, brainId, startDate, endDate]
  );

  const { data, loading, error } = useQuery<SystemInsightsPerformanceData>(
    SYSTEM_INSIGHTS_PERFORMANCE,
    { variables, skip: skip || !accountId }
  );

  const result = data?.get_system_insights_performance?.[0];

  return {
    isLoading: loading,
    error,
    avgCsatBrain: result?.avg_csat_brain ?? null,
    csatBrainCount: result?.csat_brain_count ?? 0,
    goalAchievedPercentage: result?.goal_achieved_percentage ?? null,
    goalAchievedCount: result?.goal_achieved_count ?? 0,
    goalTotalCount: result?.goal_total_count ?? 0,
  };
};
